import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import DescriptionIcon from '@mui/icons-material/Description';
import GroupsIcon from '@mui/icons-material/Groups';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import DownloadIcon from '@mui/icons-material/Download';
import useAuth from '../hooks/useAuth';
import {
  listCohorts,
  listAllStudents,
  generateTranscriptPdf,
  generateCohortSummaryPdf,
  generateOutcomeReportPdf,
} from '../services/collegeApi';

export default function ReportsPage() {
  const { user } = useAuth();
  const [cohorts, setCohorts] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snack, setSnack] = useState('');

  useEffect(() => {
    if (!user?.college_id) return;
    setLoading(true);
    Promise.all([
      listCohorts(user.college_id).catch(() => ({ cohorts: [] })),
      listAllStudents(user.college_id).catch(() => ({ students: [] })),
    ])
      .then(([c, s]) => {
        setCohorts(c?.cohorts || []);
        setStudents(s?.students || []);
      })
      .catch((err) => setError(err?.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  }, [user]);

  const onComplete = (msg) => setSnack(msg);
  const onError = (err) => setError(err?.response?.data?.error || err.message || 'Failed to generate report');

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 0.5 }}>Reports & Compliance</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Generate accreditation-ready PDFs for registrars, accreditors, and program marketing.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <ReportCard
              icon={<DescriptionIcon sx={{ fontSize: 32, color: '#2563eb' }} />}
              title="Student Transcript"
              audience="For the registrar"
              description="Per-student academic transcript with hours logged, assessments completed, mentor evaluations, and final qualification status."
              renderForm={() => (
                <TranscriptForm
                  students={students}
                  cohorts={cohorts}
                  collegeId={user.college_id}
                  onComplete={onComplete}
                  onError={onError}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <ReportCard
              icon={<GroupsIcon sx={{ fontSize: 32, color: '#7c3aed' }} />}
              title="Cohort Summary"
              audience="For accreditors (ABET, NSA/DHS CAE)"
              description="Aggregate cohort statistics: avg hours, completion rate, mentor satisfaction scores, and industry mentor demographics."
              renderForm={() => (
                <CohortReportForm
                  cohorts={cohorts}
                  collegeId={user.college_id}
                  generator={generateCohortSummaryPdf}
                  buttonLabel="Generate Cohort Summary"
                  onComplete={onComplete}
                  onError={onError}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <ReportCard
              icon={<EmojiEventsIcon sx={{ fontSize: 32, color: '#16a34a' }} />}
              title="Outcome Report"
              audience="For program marketing"
              description="Skills gained, total hours, placement rates, and opt-in alumni testimonials. Use for brochures and recruiting materials."
              renderForm={() => (
                <CohortReportForm
                  cohorts={cohorts}
                  collegeId={user.college_id}
                  generator={generateOutcomeReportPdf}
                  buttonLabel="Generate Outcome Report"
                  onComplete={onComplete}
                  onError={onError}
                />
              )}
            />
          </Grid>
        </Grid>
      )}

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack('')}
        message={snack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

function ReportCard({ icon, title, audience, description, renderForm }) {
  return (
    <Paper sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        {icon}
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>{title}</Typography>
          <Typography variant="caption" color="text.secondary">{audience}</Typography>
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flex: 1 }}>
        {description}
      </Typography>
      {renderForm()}
    </Paper>
  );
}

function TranscriptForm({ students, cohorts, collegeId, onComplete, onError }) {
  const [studentId, setStudentId] = useState('');
  const [cohortId, setCohortId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleGenerate = async () => {
    if (!studentId) return;
    setSubmitting(true);
    try {
      const result = await generateTranscriptPdf(collegeId, studentId, cohortId || undefined);
      if (result?.url) {
        window.open(result.url, '_blank', 'noopener');
        onComplete('Transcript opened in new tab');
      } else {
        onComplete('Transcript queued for generation');
      }
    } catch (err) {
      onError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack spacing={2}>
      <TextField
        select
        size="small"
        label="Student"
        value={studentId}
        onChange={(e) => setStudentId(e.target.value)}
        fullWidth
      >
        <MenuItem value=""><em>Select a student</em></MenuItem>
        {students.map((s) => (
          <MenuItem key={s.student_id} value={s.student_id}>
            {s.name || s.email}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        size="small"
        label="Cohort (optional)"
        value={cohortId}
        onChange={(e) => setCohortId(e.target.value)}
        fullWidth
      >
        <MenuItem value=""><em>All cohorts</em></MenuItem>
        {cohorts.map((c) => (
          <MenuItem key={c.cohort_id} value={c.cohort_id}>{c.name}</MenuItem>
        ))}
      </TextField>
      <Button
        variant="contained"
        startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
        onClick={handleGenerate}
        disabled={!studentId || submitting}
      >
        Generate Transcript
      </Button>
    </Stack>
  );
}

function CohortReportForm({ cohorts, collegeId, generator, buttonLabel, onComplete, onError }) {
  const [cohortId, setCohortId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleGenerate = async () => {
    if (!cohortId) return;
    setSubmitting(true);
    try {
      const result = await generator(collegeId, cohortId);
      if (result?.url) {
        window.open(result.url, '_blank', 'noopener');
        onComplete('Report opened in new tab');
      } else {
        onComplete('Report queued for generation');
      }
    } catch (err) {
      onError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack spacing={2}>
      <TextField
        select
        size="small"
        label="Cohort"
        value={cohortId}
        onChange={(e) => setCohortId(e.target.value)}
        fullWidth
      >
        <MenuItem value=""><em>Select a cohort</em></MenuItem>
        {cohorts.map((c) => (
          <MenuItem key={c.cohort_id} value={c.cohort_id}>{c.name}</MenuItem>
        ))}
      </TextField>
      <Button
        variant="contained"
        startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
        onClick={handleGenerate}
        disabled={!cohortId || submitting}
      >
        {buttonLabel}
      </Button>
    </Stack>
  );
}
